/**
 * Estado evaluado de un sensor frente a sus umbrales.
 *
 * El orden de evaluación replica exactamente el de la aplicación anterior
 * (`sensor-values.js`): críticos antes que alertas, y la ausencia de umbrales
 * o de lecturas se distingue de un estado normal.
 */

export const ESTADOS_SENSOR = ['normal', 'alerta', 'critico', 'sin_umbrales', 'sin_datos'] as const;

export type EstadoSensorValor = (typeof ESTADOS_SENSOR)[number];

const ETIQUETA: Readonly<Record<EstadoSensorValor, string>> = {
  normal: 'Normal',
  alerta: 'Fuera de rango',
  critico: 'En alarma',
  sin_umbrales: 'Sin umbral configurado',
  sin_datos: 'Sin lecturas',
};

const TOKEN: Readonly<Record<EstadoSensorValor, string>> = {
  normal: '--status-success',
  alerta: '--status-warning',
  critico: '--status-danger',
  sin_umbrales: '--text-3',
  sin_datos: '--text-3',
};

/** Mayor número = más urgente. Sirve para ordenar listas por gravedad. */
const URGENCIA: Readonly<Record<EstadoSensorValor, number>> = {
  critico: 4,
  alerta: 3,
  sin_datos: 2,
  sin_umbrales: 1,
  normal: 0,
};

export class EstadoSensor {
  private constructor(readonly valor: EstadoSensorValor) {}

  static normal(): EstadoSensor {
    return new EstadoSensor('normal');
  }
  static alerta(): EstadoSensor {
    return new EstadoSensor('alerta');
  }
  static critico(): EstadoSensor {
    return new EstadoSensor('critico');
  }
  static sinUmbrales(): EstadoSensor {
    return new EstadoSensor('sin_umbrales');
  }
  static sinDatos(): EstadoSensor {
    return new EstadoSensor('sin_datos');
  }

  static from(raw: string | null | undefined): EstadoSensor {
    const limpio = (raw ?? '').trim().toLowerCase().replace(/-/g, '_');
    const valor = (ESTADOS_SENSOR as readonly string[]).includes(limpio)
      ? (limpio as EstadoSensorValor)
      : 'sin_datos';
    return new EstadoSensor(valor);
  }

  get etiqueta(): string {
    return ETIQUETA[this.valor];
  }

  get color(): string {
    return `var(${TOKEN[this.valor]})`;
  }

  get urgencia(): number {
    return URGENCIA[this.valor];
  }

  /** El sensor está midiendo y dentro de lo esperado. */
  get esNormal(): boolean {
    return this.valor === 'normal';
  }

  /** Requiere que alguien mire: alerta o crítico. */
  get requiereAtencion(): boolean {
    return this.valor === 'alerta' || this.valor === 'critico';
  }

  /** No hay información suficiente para juzgar (sin umbral o sin lectura). */
  get esIndeterminado(): boolean {
    return this.valor === 'sin_umbrales' || this.valor === 'sin_datos';
  }

  equals(otro: EstadoSensor): boolean {
    return this.valor === otro.valor;
  }

  static comparar(a: EstadoSensor, b: EstadoSensor): number {
    return b.urgencia - a.urgencia;
  }

  toString(): string {
    return this.valor;
  }
}
